import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { TokenStorage } from '@/api/tokenStorage'
import { Link } from '@/components/Link'
import { useNavItems, type NavItem } from '@/components/navItems'
import { ThemeToggle } from '@/components/theme-toggle'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SectionId } from '@/routes/Search/SearchPage'
import { persistor } from '@/store/store'
import { useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { useUIVersion } from '@/utils/UIVersionContext'
import { WebSocketContext } from '@/utils/WebSocketContext'
import { LogoIcon } from 'components/Icon'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command'
import { cn } from 'lib/utils'
import {
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  LogOut,
  Search,
  Target,
  User,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react'
import { AuthSessionContext } from 'utils/AuthContext'
import { appENV, checkWindowLocation } from 'utils/dom'
import { links } from 'utils/featureToggles'

export const AppSidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [, setAuth] = useContext(AuthSessionContext)
  const { hub } = useContext(WebSocketContext)
  const identityApiClient = useApiClient(IdentityApiClient)
  const { menuPosition } = useUIVersion()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const handleLogout = async () => {
    const sessionId = TokenStorage.getSession()
    await hub?.client.stop()
    await identityApiClient.signOut(sessionId)
    setAuth(null)

    persistor.purge()

    navigate(links.login.url)
  }

  const handleSearch = (query: string, section?: SectionId) => {
    if (!query.trim()) return

    const params = new URLSearchParams({
      q: query.trim(),
    })

    if (section) {
      params.set('section', section)
    }

    navigate(`/search?${params.toString()}`)
    setSearchOpen(false)
    setSearchQuery('')
  }

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const visibleNavItems = useNavItems()

  const isItemActive = (item: NavItem): boolean => {
    if (item.href && location.pathname.startsWith(item.href)) return true
    if (item.children) {
      return item.children.some((child) => isItemActive(child))
    }
    return false
  }

  const currentEnv = checkWindowLocation()
  const envLabel =
    currentEnv === appENV.localhost || currentEnv === appENV.development
      ? 'DEV'
      : currentEnv === appENV.test
        ? 'TESTING'
        : 'PROD'

  const [expanded, setExpanded] = useState(false)
  const isTopMenu = menuPosition === 'top'
  const isRightMenu = menuPosition === 'right'
  const isExpanded = isTopMenu || expanded
  const dropdownSide = isTopMenu ? 'bottom' : isRightMenu ? 'left' : 'right'
  const isProfileActive = location.pathname.startsWith('/profile')
  const activeIndicatorClassName = cn(
    'absolute bg-primary',
    isTopMenu
      ? 'bottom-0 left-3 right-3 h-1 rounded-t-full'
      : 'left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full'
  )
  const sidebarTriggerContentClassName = cn(
    'flex h-full items-center',
    isTopMenu ? 'flex-col justify-center gap-1' : 'gap-2'
  )
  const sidebarTriggerIconClassName = 'size-5 shrink-0'
  const sidebarTriggerLabelClassName = cn(
    'text-center leading-tight',
    isTopMenu && 'max-w-20 whitespace-normal text-xs'
  )

  const getSidebarTriggerClassName = ({
    isActive = false,
    fitContainer = false,
  }: {
    isActive?: boolean
    fitContainer?: boolean
  } = {}) =>
    cn(
      'relative flex w-full',
      isTopMenu
        ? cn(
            'h-full flex-col items-center justify-center px-1.5',
            fitContainer ? 'max-w-full' : 'min-w-16 max-w-24'
          )
        : 'items-center justify-start',
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    )

  const renderInlineBadge = (badge?: number) =>
    badge !== undefined && badge > 0 ? (
      <span className='flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white'>
        {badge}
      </span>
    ) : null

  const renderDropdownItems = (items: NavItem[]): React.ReactNode =>
    items.map((item) => {
      const Icon = item.icon
      const isActive = isItemActive(item)
      const childItems = item.children ?? []
      const hasChildren = childItems.length > 0

      if (hasChildren) {
        return (
          <DropdownMenuSub key={item.title}>
            <DropdownMenuSubTrigger
              className={cn('cursor-pointer gap-3', isActive && 'bg-accent text-accent-foreground')}
            >
              <Icon className='h-4 w-4 shrink-0' />
              <span className='flex-1 truncate'>{item.title}</span>
              {renderInlineBadge(item.badge)}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent
              side={isRightMenu ? 'left' : 'right'}
              className='w-64 cursor-pointer'
            >
              {renderDropdownItems(childItems)}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )
      }

      if (!item.href) return null

      return (
        <Link to={item.href} className='text-inherit!'>
          <DropdownMenuItem
            key={item.title}
            className={cn('gap-3', isActive && 'bg-accent text-accent-foreground')}
          >
            <Icon className='h-4 w-4 shrink-0' />
            <span className='flex-1 truncate'>{item.title}</span>
            {renderInlineBadge(item.badge)}
          </DropdownMenuItem>
        </Link>
      )
    })

  return (
    <div className={cn('flex shrink-0', isTopMenu ? 'h-16 w-full' : 'h-screen')}>
      <div
        className={cn(
          'flex bg-sidebar',
          isTopMenu
            ? 'h-full w-full flex-row border-b border-sidebar-border'
            : 'transition-[width] flex-col border-sidebar-border',
          !isTopMenu && (isRightMenu ? 'border-l' : 'border-r'),
          !isTopMenu && (expanded ? 'w-45' : 'w-15')
        )}
      >
        <div
          className={cn(
            'relative flex items-center justify-center border-sidebar-border',
            isTopMenu ? 'h-full w-16 border-r' : 'h-16 border-b'
          )}
        >
          <Link to='/'>
            <LogoIcon />
          </Link>

          <span
            className={cn(
              'absolute rounded-full px-1 py-0.5 text-[8px] font-bold',
              isTopMenu ? 'bottom-1 right-[50%] translate-x-[50%] shadow-xs' : 'bottom-1',
              currentEnv === appENV.localhost || currentEnv === appENV.development
                ? 'bg-accent text-accent-foreground'
                : currentEnv === appENV.test
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-red-100 text-red-700'
            )}
          >
            {envLabel}
          </span>
        </div>

        <div
          className={cn(
            'border-sidebar-border p-2',
            isTopMenu ? 'flex w-16 items-stretch border-r' : 'border-b'
          )}
        >
          <Button
            variant='ghost'
            type='button'
            className={cn(
              'w-full',
              isTopMenu ? 'h-full flex-col justify-center gap-0.5 px-1 text-xs' : 'justify-start'
            )}
            onClick={() => setSearchOpen(true)}
          >
            <Search className={'size-5'} />
            {isExpanded && <span className='text-center leading-tight'>Search</span>}
          </Button>

          {!isTopMenu && (
            <div className='mt-2 text-center text-[10px] text-muted-foreground/70'>
              <kbd className='rounded border border-border bg-background px-1 py-0.5 text-[9px] shadow-sm'>
                ⌘K
              </kbd>
            </div>
          )}
        </div>

        <div
          className={cn(
            'flex-1',
            isTopMenu
              ? 'overflow-x-auto overflow-y-hidden pt-1.5'
              : 'overflow-y-auto overflow-x-clip py-4'
          )}
        >
          <nav
            className={cn(
              'flex gap-1 px-2',
              isTopMenu ? 'h-full flex-row justify-end' : 'flex-col'
            )}
          >
            {visibleNavItems.map((item) => {
              const Icon = item.icon
              const isActive = isItemActive(item)
              const childItems = item.children ?? []
              const hasChildren = childItems.length > 0
              const buttonContent = (
                <>
                  {isActive && <div className={activeIndicatorClassName} />}
                  <div className={sidebarTriggerContentClassName}>
                    <Icon className={sidebarTriggerIconClassName} />
                    {isExpanded && (
                      <span className={sidebarTriggerLabelClassName}>{item.title}</span>
                    )}
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className='absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[12px] text-white'>
                      {item.badge}
                    </span>
                  )}
                </>
              )

              return (
                <div key={item.title} className={cn(isTopMenu && 'h-full shrink-0')}>
                  {hasChildren ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        openOnHover
                        delay={25}
                        render={
                          <Button
                            variant='ghost'
                            size='lg'
                            className={getSidebarTriggerClassName({ isActive })}
                            aria-label={item.title}
                            title={item.title}
                          />
                        }
                      >
                        {buttonContent}
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        side={dropdownSide}
                        align='start'
                        sideOffset={8}
                        className='w-64 cursor-pointer'
                      >
                        <DropdownMenuGroup className='cursor-pointer'>
                          <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
                          {renderDropdownItems(childItems)}
                        </DropdownMenuGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link to={item.href} className='text-inherit!'>
                      <Button
                        variant='ghost'
                        size='lg'
                        className={getSidebarTriggerClassName({ isActive })}
                        aria-label={item.title}
                        title={item.title}
                      >
                        {buttonContent}
                      </Button>
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        <div
          className={cn(
            'flex justify-center border-sidebar-border',
            isTopMenu ? 'w-16 border-l pt-1.5' : 'p-2'
          )}
        >
          <ThemeToggle
            side={dropdownSide}
            align='end'
            expanded={isExpanded}
            labelPlacement={isTopMenu ? 'below' : 'inline'}
            triggerClassName={getSidebarTriggerClassName({ fitContainer: true })}
            triggerSize='lg'
            contentClassName={sidebarTriggerContentClassName}
            iconClassName={sidebarTriggerIconClassName}
            labelClassName={sidebarTriggerLabelClassName}
          />
        </div>

        <div
          className={cn(
            'border-sidebar-border',
            isTopMenu ? 'w-16 border-l pt-1.5' : 'border-t p-2'
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger
              delay={25}
              openOnHover
              render={
                <Button
                  variant='ghost'
                  size='lg'
                  type='button'
                  className={getSidebarTriggerClassName({
                    isActive: isProfileActive,
                    fitContainer: true,
                  })}
                  aria-label='Profile'
                  title='Profile'
                />
              }
            >
              {isProfileActive && <div className={activeIndicatorClassName} />}
              <div className={sidebarTriggerContentClassName}>
                <User className={sidebarTriggerIconClassName} />
                {isExpanded && <span className={sidebarTriggerLabelClassName}>Profile</span>}
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent side={dropdownSide} align='end'>
              <Link to={links.profileSettings.url} className='text-inherit!'>
                <DropdownMenuItem>
                  <User className='h-4 w-4' />
                  <span>Profile</span>
                </DropdownMenuItem>
              </Link>

              <DropdownMenuItem onClick={handleLogout} className='flex items-center gap-2'>
                <LogOut className='h-4 w-4' />
                <span className='text-red-600'>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {!isTopMenu && (
          <div className='border-t flex justify-center border-sidebar-border p-2'>
            <Button
              variant='ghost'
              className='w-full justify-start'
              type='button'
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronLeft /> : <ChevronRight />}
              {expanded && <span className='text-center leading-tight'>Collapse</span>}
            </Button>
          </div>
        )}
      </div>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput
          autoFocus
          placeholder='Search for clients, wallets, transactions, IB, leads...'
          value={searchQuery}
          onValueChange={setSearchQuery}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchQuery.trim()) {
              handleSearch(searchQuery)
            }
          }}
        />

        <CommandList>
          {!searchQuery.trim() ? (
            <CommandEmpty>
              <div className='py-6 text-center text-sm'>
                <Search className='mx-auto mb-2 h-10 w-10 text-muted-foreground' />
                <p className='text-muted-foreground'>Type to search across the system</p>
                <p className='mt-1 text-xs text-muted-foreground/80'>
                  Search for clients, wallets, trading accounts, transactions, and more
                </p>
              </div>
            </CommandEmpty>
          ) : null}

          <CommandGroup heading='Quick Actions'>
            <CommandItem
              onSelect={() => handleSearch(searchQuery)}
              disabled={!searchQuery.trim()}
              className='flex items-center gap-3 text-left'
            >
              <Search className='h-4 w-4' />
              <div className='flex-1'>
                <div className='font-medium'>Search for "{searchQuery || '...'}"</div>
                <div className='text-xs text-muted-foreground'>Press Enter to search</div>
              </div>
            </CommandItem>
          </CommandGroup>

          {searchQuery && (
            <CommandGroup heading='Search Categories'>
              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'client')}
                className='flex items-center gap-3 text-left'
              >
                <Users className='h-4 w-4 text-blue-500' />
                <div>
                  <div className='font-medium'>Clients</div>
                  <div className='text-xs text-muted-foreground'>Search client records</div>
                </div>
              </CommandItem>

              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'wallet')}
                className='flex items-center gap-3 text-left'
              >
                <Wallet className='h-4 w-4 text-green-500' />
                <div>
                  <div className='font-medium'>Wallets</div>
                  <div className='text-xs text-muted-foreground'>Search wallet records</div>
                </div>
              </CommandItem>

              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'trading_account')}
                className='flex items-center gap-3 text-left'
              >
                <CreditCard className='h-4 w-4 text-accent-foreground' />
                <div>
                  <div className='font-medium'>Trading Accounts</div>
                  <div className='text-xs text-muted-foreground'>Search trading accounts</div>
                </div>
              </CommandItem>

              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'transaction')}
                className='flex items-center gap-3 text-left'
              >
                <ArrowLeftRight className='h-4 w-4 text-orange-500' />
                <div>
                  <div className='font-medium'>Transactions</div>
                  <div className='text-xs text-muted-foreground'>Search transaction history</div>
                </div>
              </CommandItem>

              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'introducing_broker')}
                className='flex items-center gap-3 text-left'
              >
                <UserPlus className='h-4 w-4 text-indigo-500' />
                <div>
                  <div className='font-medium'>Introducing Brokers</div>
                  <div className='text-xs text-muted-foreground'>Search IB records</div>
                </div>
              </CommandItem>

              <CommandItem
                onSelect={() => handleSearch(searchQuery, 'lead')}
                className='flex items-center gap-3 text-left'
              >
                <Target className='h-4 w-4 text-pink-500' />
                <div>
                  <div className='font-medium'>Leads</div>
                  <div className='text-xs text-muted-foreground'>Search lead records</div>
                </div>
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  )
}
